import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {environment} from "@environment";
import {ImageService, ToastService} from "@services";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import { FullCropperWidthComponent } from '@components';
import { TOAST_STATE } from '@model';

@Component({
  selector: 'app-drag-and-drop',
  templateUrl: './drag-and-drop.component.html',
  styleUrls: ['./drag-and-drop.component.scss']
})
export class DragAndDropComponent implements OnChanges, OnInit {

  @Input() useWithCropper: boolean = false;
  @Input() fileType: FileType = FileType.IMAGE
  @Input() selectedFile: string | undefined;
  @Input() isDisabled: boolean = false;
  @Input() isFullHeight: boolean = false;
  @Input() cropAreaWidth: number = 396;
  @Input() cropAreaHeight: number = 290;
  @Output() fileChange: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();
  @ViewChild("file") input?: ElementRef;

  fileName: string | undefined;

  constructor(
    private toastService: ToastService,
    private translocoService: TranslocoService,
    private modalService: NgbModal,
    private imageService: ImageService
  ) {
  }

  ngOnInit(): void {
    if (this.fileType === FileType.IMAGE && this.selectedFile && this.selectedFile.startsWith("http")) {
      this.imageService.downloadImage(this.selectedFile, (base64data:string)=>{
        this.selectedFile = base64data;
        this.fileChange.emit(base64data);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['selectedFile']?.currentValue) {
      if (this.input) {
        this.input.nativeElement.value = "";
      }
    }
  }

  processFile(fileInput: any): void {
    if (fileInput.files?.length > 0) {
      const file: File = fileInput.files[0];
      if (!this.checkFileType(file)) {
        return;
      }
      if (file.size > environment.maxFileSize) {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.user.profile.max.size.error"));
      } else {
        this.fileName = file.name
        const reader: FileReader = new FileReader();
        const img: HTMLImageElement = new Image();
        if (this.fileType === FileType.IMAGE) {
          img.src = window.URL.createObjectURL(file);
        }
        reader.addEventListener('load', (event: any) => {
          // this should be uncommented to include in image size validation
          /*          if (this.fileType === FileType.IMAGE) {
                      console.log("Width: " + img.naturalWidth);
                      console.log("Height: " + img.naturalHeight);
                    }*/
          this.fileChange.emit(event.target.result);
        });
        reader.readAsDataURL(file);
      }
    }
  }

  checkFileType(file: File): boolean {
    if (this.fileType === FileType.IMAGE) {
      return file.type.startsWith('image/')
    } else if (this.fileType === FileType.XLS) {
      return file.type === 'application/vnd.ms-excel' ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

    }
    return false;
  }

  onDeleteFile(): void {
    this.selectedFile = undefined;
    this.fileChange.emit(undefined);
  }

  getUploadText() {
    switch (this.fileType) {
      case FileType.IMAGE:
        if (this.useWithCropper) {
          return this.translocoService.translate('app.common.select.photo.to.crop');
        } else {
          return this.translocoService.translate('app.common.upload.photo');
        }
      case FileType.XLS:
        return "UPLOAD xLS";
      default:
        return "";
    }
  }

  accept() {
    switch (this.fileType) {
      case FileType.IMAGE:
        return "image/*";
      case FileType.XLS:
        return ".xls, .xlsx";
      default:
        return "";
    }
  }

  openImageCropper() {
    const modalRef: NgbModalRef = this.modalService.open(FullCropperWidthComponent, {
      centered: true,
      windowClass: 'drag-drop-modal',
    });

    modalRef.componentInstance.title = this.translocoService.translate("app.common.upload.and.crop.photo");
    modalRef.componentInstance.cropAreaWidth = this.cropAreaWidth;
    modalRef.componentInstance.cropAreaHeight = this.cropAreaHeight;

    modalRef.result.then((croppedImage: any) => {
      if (croppedImage !== undefined && croppedImage !== '') {
        this.selectedFile = croppedImage;
        this.fileChange.emit(croppedImage);
      }
    }, (_) => {
      /// ignore
    });
  }

  protected readonly FileType = FileType;
}

export enum FileType {
  XLS = 1,
  IMAGE = 2,
}
